import monthCellHeaderTextFormatter from '../utils/monthCellHeaderTextFormatter';
import TimeConstants from 'owa-datetime-utils/lib/TimeConstants';
import {
    addDays,
    endOfWeek,
    getDateString,
    getMonth,
    isSameDay,
    OwaDate,
    startOfMonth,
} from 'owa-datetime';
import { CalendarMonthDayCellModel } from '../store/schema/CalendarMonthDayCellModel';
import { CalendarMonthTransformProps } from '../MonthSurfaceTransform';
import { createCalendarMonthDayCell, createCalendarWeekNumber } from '../Templates';
import { getWeeksInMonth, getWeekNumber } from 'owa-datetime-utils';
import { SurfaceConstants } from 'owa-calendar-surface-core';
import {
    CalendarSurfaceItemsConfig,
    CalendarSurfaceStore,
    ColumnProvider,
    SurfaceItem,
    SurfaceMonthItemState,
    generateOverflowItems,
    convertAllDayLayoutPositionToStyles,
    convertLayoutPositionToStyles,
    getHorizontalLayout,
    LayoutPositionDetails,
    RowItemsCollection,
    wrapToGridItem,
} from 'owa-calendar-surface-dategrid';
import {
    getAllowedResizeTypesForMonthItem,
    getDisplayMonthRangeStart,
    makeDraggable,
} from 'owa-calendar-surface-dategrid-common-dnd';

export interface OverflowIndicator {
    layout: LayoutPositionDetails;
    item: SurfaceItem;
}

/**
 * Generates month grid day cells
 * @param config the configuration for calendar month view
 * @param store The surface store
 */
export default function generateMonthGrid(
    config: CalendarMonthTransformProps,
    store: CalendarSurfaceStore,
    hideTodayEmphasis: boolean
) {
    let date = getDisplayMonthRangeStart(store);
    let month = getMonth(store.dateRange.start);
    let weeksInMonth = getWeeksInMonth(store.dateRange.start, store.userOptions.weekStartDay);

    let results: JSX.Element[] = [];
    for (let i = 0; i < weeksInMonth; i++) {
        for (let j = 0; j < TimeConstants.DaysInOneWeek; j++) {
            let left = j / TimeConstants.DaysInOneWeek;
            let right = (TimeConstants.DaysInOneWeek - j - 1) / TimeConstants.DaysInOneWeek;
            let isRTL = store.userOptions.isRTL;
            let layout: LayoutPositionDetails = {
                top: i / weeksInMonth,
                left: isRTL ? right : left,
                right: isRTL ? left : right,
                height: 1 / weeksInMonth,
            };
            let dayCellModel: CalendarMonthDayCellModel = { date: date, month: month };
            let reactKey = getDateString(date);
            let tabKey = 'surfaceMonthHeader_' + reactKey;

            results.push(
                wrapToGridItem(
                    convertLayoutPositionToStyles(layout),
                    reactKey,
                    config.monthDayCellsConfig,
                    dayCellModel,
                    createCalendarMonthDayCell(
                        date,
                        isSameDay(date, store.selectedDate),
                        hideTodayEmphasis,
                        monthCellHeaderTextFormatter(
                            getDisplayMonthRangeStart(store),
                            config.shouldShowWeekMonthName
                        ),
                        config.renderDateHeaderAddOn
                    ),
                    null /* item index */,
                    tabKey /* tab Id */
                )
            );

            date = addDays(date, 1);
        }
    }

    return results;
}

/**
 * Creates calendar items content for month view
 * @param store The surface store
 * @param config the configuration for calendar month view
 * @param resolvedItems collection of items grouped by rows
 * @param columnProvider The ColumnProvider
 * @param week the week number
 * @param maxRows maximum number of visible rows
 * @returns ready-to-be-mounted items content for month view
 */
export function generateWeekItems(
    store: CalendarSurfaceStore,
    config: CalendarMonthTransformProps,
    resolvedItems: RowItemsCollection,
    columnProvider: ColumnProvider,
    week: number,
    maxRows: number
): JSX.Element[] {
    let result: JSX.Element[] = [];

    const transformMonthOverflowItem = (item: SurfaceItem, rowIndex: number): JSX.Element => {
        let wrappedItem = createMonthSurfaceItem(
            store,
            config.getItemConfig?.(item.model),
            columnProvider,
            item,
            week,
            rowIndex
        );
        let allowedResizeTypes = getAllowedResizeTypesForMonthItem(
            store,
            config.dragDropConfig,
            item.model,
            week
        );
        return makeDraggable(store, config.dragDropConfig, item, wrappedItem, allowedResizeTypes);
    };

    // Use the resolved rows of items, calculate layout and create react components
    let weekVisibleItems: SurfaceItem[][] = resolvedItems.VisibleRows;
    result.push(
        ...generateVisibleWeekItems(
            store,
            config,
            weekVisibleItems,
            columnProvider,
            store.monthHeight,
            week
        )
    );

    let hiddenItems: SurfaceItem[] = resolvedItems.HiddenItems;
    const weeksInMonth = getWeeksInMonth(store.dateRange.start, store.userOptions.weekStartDay);

    // Below the SurfaceConstants.minimumScalableMonthHeight we need to scale (non-linearly)
    // the padding to support smaller window sizes and browser zoom
    const monthHeight = store.monthHeight;
    const paddingScaleFactor =
        monthHeight < SurfaceConstants.minimumScalableMonthHeight
            ? monthHeight /
              Math.pow(
                  SurfaceConstants.minimumScalableMonthHeight,
                  SurfaceConstants.minimumScalableMonthHeight / monthHeight
              )
            : 1;

    const weekHeight = monthHeight / weeksInMonth;

    // disable overflow optimization if
    //   - there is no room for any visible rows AND
    //   - the minimum "visual space" necessary to render day cell header + single overflow row for a week is not available
    const disableOverflowOptimization =
        maxRows === 0 &&
        weekHeight < SurfaceConstants.DayCellHeaderHeight + SurfaceConstants.MonthRowHeight;

    result.push(
        ...generateOverflowItems(
            store,
            config,
            hiddenItems,
            transformMonthOverflowItem,
            columnProvider,
            week,
            maxRows,
            SurfaceConstants.MonthRowHeight,
            weekHeight,
            SurfaceConstants.DayCellHeaderHeight * paddingScaleFactor,
            disableOverflowOptimization
        )
    );

    return result;
}

/**
 * Creates the JSX element for a single month surface item (no unfolding applied)
 * @param store The surface store
 * @param itemsConfigTable the table of configurations for items
 * @param columnProvider The column provider
 * @param item The surface item
 * @param week The week index
 * @param rowIndex The row index within the week for the item
 * @returns The JSX element for the month day item
 */
export function createMonthSurfaceItem(
    store: CalendarSurfaceStore,
    itemConfig: CalendarSurfaceItemsConfig,
    columnProvider: ColumnProvider,
    item: SurfaceItem,
    week: number,
    rowIndex: number
): JSX.Element {
    let weeksInMonth = getWeeksInMonth(store.dateRange.start, store.userOptions.weekStartDay);
    let layout = calculateMonthItemLayout(
        item,
        columnProvider,
        store.monthHeight,
        week,
        rowIndex,
        weeksInMonth,
        columnProvider.columnCount,
        store.dayLength,
        store.userOptions.isRTL
    );

    let itemState: SurfaceMonthItemState = {
        week: week,
    };

    return wrapToGridItem(
        convertAllDayLayoutPositionToStyles(layout),
        item.key,
        itemConfig,
        item.model,
        itemConfig?.template?.(store, item.model, itemState),
        week,
        item.key
    );
}

function calculateMonthItemLayout(
    item: SurfaceItem,
    columnProvider: ColumnProvider,
    monthHeight: number,
    week: number,
    rowIndex: number,
    weeksInMonth: number,
    columnCount: number,
    columnLength: number,
    isRTL: boolean
) {
    return {
        ...getHorizontalLayout(item, columnProvider, columnLength, isRTL),
        top:
            (monthHeight / weeksInMonth) * week +
            rowIndex * SurfaceConstants.MonthRowHeight +
            SurfaceConstants.DayCellHeaderHeight,
        height: SurfaceConstants.MonthItemHeight,
    };
}

function generateVisibleWeekItems(
    store: CalendarSurfaceStore,
    config: CalendarMonthTransformProps,
    weekVisibleItems: SurfaceItem[][],
    columnProvider: ColumnProvider,
    monthHeight: number,
    week: number
): JSX.Element[] {
    let result: JSX.Element[] = [];

    weekVisibleItems.forEach((rowItems, rowIndex) => {
        rowItems.forEach(item => {
            let wrappedItem = createMonthSurfaceItem(
                store,
                config.getItemConfig?.(item.model),
                columnProvider,
                item,
                week,
                rowIndex
            );
            let allowedResizeTypes = getAllowedResizeTypesForMonthItem(
                store,
                config.dragDropConfig,
                item.model,
                week
            );
            result.push(
                makeDraggable(store, config.dragDropConfig, item, wrappedItem, allowedResizeTypes)
            );
        });
    });

    return result;
}

/** Generates month view row headers, showing week numbers */
export function createMonthRowHeaders(
    date: OwaDate,
    firstDayOfWeek: number,
    firstWeekOfYear: number
): JSX.Element[] {
    let results: JSX.Element[] = [];
    let weekEnd = endOfWeek(startOfMonth(date), firstDayOfWeek);
    let weeksInMonth = getWeeksInMonth(date, firstDayOfWeek);
    for (let i = 0; i < weeksInMonth; i++) {
        let weekNumber = getWeekNumber(weekEnd, firstDayOfWeek, firstWeekOfYear);
        results.push(createCalendarWeekNumber(weekNumber, weeksInMonth));
        weekEnd = addDays(weekEnd, TimeConstants.DaysInOneWeek);
    }
    return results;
}
